import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitives from "@radix-ui/react-dialog";
import posthog from "posthog-js";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MdDragIndicator } from "react-icons/md";
import PatientSearch from "@/components/patient/patient-search";
import { Button, Label, Switch, Textarea } from "@/components/ui";
import { DialogBody, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useDraggable } from "@/hooks/use-draggable";
import { useEventTitle } from "@/hooks/use-event-title";
import { createDraftMemo } from "@/lib/create-draft-memo";
import { formatDate, formatDateTime, formatName } from "@/lib/format-data";
import { cx, focusRing } from "@/lib/tailwind-utils";
import { UserType } from "@/types/enums/user-type";
import { EventDetail, EventTiny } from "@/types/event";
import { PatientSummary } from "@/types/patient";
import { QuickNoteSummary, QuickNoteUpdate } from "@/types/quick-note";
import { UserTiny } from "@/types/user";
import { useCreateQuickNoteMutation, useUpdateQuickNoteMutation } from "@/api/provider/user/patient/quick-note-mutation";
import { quickNoteQueryKeys } from "@/api/provider/user/patient/quick-note-query";
import { invalidateByKey } from "@/api/utils/query-helpers";
interface QuickNoteDialogProps {
  event?: EventTiny | EventDetail;
  /** Patient to create note for - if not provided, will show patient search */
  patient?: UserTiny;
  existingNote?: QuickNoteSummary;
  /**
   * Keep create/edit in memory instead of hitting the API. Used by the
   * appointment-create form, where a memo must not be persisted (as an
   * account-level note with no event_id) until the appointment exists — the
   * form creates it with the event_id once the appointment is saved.
   */
  deferPersistence?: boolean;
  /**
   * Leave the page usable while the window is open. Only for windows that
   * outlive whatever opened them (rendered at the console layout), so moving
   * around the app can't unmount them and lose the draft. Everywhere else the
   * window stays modal.
   */
  nonModal?: boolean;
  onNoteCreate?: (note: QuickNoteSummary) => void;
  onNoteUpdate?: (note: QuickNoteSummary) => void;
  onNoteDelete?: () => void;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
interface NoteFormData {
  note: string;
  isAlert: boolean;
}
export default function QuickNoteDialog(t0) {
  const $ = _c(124);
  const {
    event,
    patient: patientProp,
    existingNote,
    deferPersistence: t1,
    nonModal: t2,
    onNoteCreate,
    onNoteUpdate,
    children,
    open: externalOpen,
    onOpenChange: externalOnOpenChange
  } = t0;
  const deferPersistence = t1 === undefined ? false : t1;
  const nonModal = t2 === undefined ? false : t2;
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const showAlertToggle = Boolean(tenant?.memoAlertPopupEnabled);
  const getEventTitle = useEventTitle();
  const createQuickNoteMutation = useCreateQuickNoteMutation();
  const updateQuickNoteMutation = useUpdateQuickNoteMutation();
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = externalOnOpenChange || setInternalOpen;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      viewportPadding: 8
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    containerRef,
    position,
    setPosition,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel
  } = useDraggable(t3);
  const windowRef = useRef(null);
  let t4;
  if ($[1] !== containerRef) {
    t4 = el => {
      windowRef.current = el;
      containerRef(el);
      if (el) {
        el.style.touchAction = "";
      }
    };
    $[1] = containerRef;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const setWindowRef = t4;
  let t5;
  let t6;
  if ($[3] !== isOpen || $[4] !== setPosition) {
    t5 = () => {
      if (!isOpen) {
        setPosition(undefined);
      }
    };
    t6 = [isOpen, setPosition];
    $[3] = isOpen;
    $[4] = setPosition;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== event?.attendees) {
    t7 = event?.attendees?.find(_temp)?.user;
    $[7] = event?.attendees;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const eventPatient = t7;
  const patient = patientProp || eventPatient || selectedPatient;
  let t8;
  if ($[9] !== event || $[10] !== getEventTitle) {
    t8 = event ? getEventTitle(event) : "";
    $[9] = event;
    $[10] = getEventTitle;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const appointmentTitle = t8;
  const showPatientSearch = !patientProp && !eventPatient;
  const targetKey = `${patientProp?.id ?? eventPatient?.id ?? ""}|${event?.id ?? ""}`;
  const openedTargetRef = useRef(null);
  let t10;
  let t9;
  if ($[12] !== isOpen || $[13] !== setIsOpen || $[14] !== targetKey) {
    t9 = () => {
      if (!isOpen) {
        openedTargetRef.current = null;
        return;
      }
      if (openedTargetRef.current === null) {
        openedTargetRef.current = targetKey;
      } else {
        if (openedTargetRef.current !== targetKey) {
          setIsOpen(false);
        }
      }
    };
    t10 = [isOpen, targetKey, setIsOpen];
    $[12] = isOpen;
    $[13] = setIsOpen;
    $[14] = targetKey;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  const noteFieldId = useId();
  const alertFieldId = useId();
  let t11;
  let t12;
  if ($[17] !== isOpen || $[18] !== showPatientSearch) {
    t11 = () => {
      if (!isOpen && showPatientSearch) {
        setSelectedPatient(undefined);
      }
    };
    t12 = [isOpen, showPatientSearch];
    $[17] = isOpen;
    $[18] = showPatientSearch;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  const t13 = existingNote?.note || "";
  const t14 = existingNote?.isAlert ?? false;
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = {
      defaultValues: {
        note: t13,
        isAlert: t14
      }
    };
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const {
    control,
    handleSubmit,
    formState: t16,
    reset
  } = useForm(t15);
  const {
    errors
  } = t16;
  let t17;
  if ($[24] !== existingNote?.isAlert || $[25] !== existingNote?.note || $[26] !== isOpen || $[27] !== reset) {
    t17 = () => {
      if (isOpen) {
        reset({
          note: existingNote?.note || "",
          isAlert: existingNote?.isAlert ?? false
        });
      }
    };
    $[24] = existingNote?.isAlert;
    $[25] = existingNote?.note;
    $[26] = isOpen;
    $[27] = reset;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = existingNote?.id;
  let t19;
  if ($[29] !== isOpen || $[30] !== reset || $[31] !== t18) {
    t19 = [isOpen, t18, reset];
    $[29] = isOpen;
    $[30] = reset;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  useEffect(t17, t19);
  let t20;
  if ($[33] !== createQuickNoteMutation || $[34] !== currentUser || $[35] !== deferPersistence || $[36] !== event?.id || $[37] !== existingNote || $[38] !== onNoteCreate || $[39] !== onNoteUpdate || $[40] !== patient || $[41] !== setIsOpen || $[42] !== showAlertToggle || $[43] !== updateQuickNoteMutation) {
    t20 = async data => {
      if (!patient || !currentUser) {
        return;
      }
      setIsSubmitting(true);
      const becameAlert = showAlertToggle && data.isAlert && !existingNote?.isAlert;
      const captureFlagged = () => {
        if (becameAlert) {
          posthog.capture("memo_alert_flagged", {
            is_new: !existingNote
          });
        }
      };
      if (deferPersistence) {
        if (existingNote && !onNoteUpdate || !existingNote && !onNoteCreate) {
          setIsSubmitting(false);
          return;
        }
        const draftMemo = createDraftMemo({
          note: data.note,
          patient: patient as QuickNoteSummary["patient"],
          creator: currentUser,
          eventId: event?.id ?? null,
          isAlert: showAlertToggle ? data.isAlert : undefined,
          existingMemo: existingNote
        });
        if (existingNote && onNoteUpdate) {
          onNoteUpdate(draftMemo);
        } else {
          if (onNoteCreate) {
            onNoteCreate(draftMemo);
          }
        }
        captureFlagged();
        setIsOpen(false);
        setIsSubmitting(false);
        return;
      }
      if (existingNote) {
        await updateQuickNoteMutation.mutateAsync({
          noteId: existingNote.id,
          input: {
            note: data.note,
            ...(showAlertToggle && {
              isAlert: data.isAlert
            })
          } as QuickNoteUpdate,
          patientId: patient.id
        }, {
          onSuccess: note => {
            invalidateByKey(quickNoteQueryKeys.list(patient.id));
            if (note && onNoteUpdate) {
              onNoteUpdate(note);
            }
            captureFlagged();
            setIsOpen(false);
          }
        });
      } else {
        await createQuickNoteMutation.mutateAsync({
          patientId: patient.id,
          note: data.note,
          eventId: event?.id,
          creatorId: currentUser.id,
          creator: currentUser,
          isAlert: showAlertToggle ? data.isAlert : false
        }, {
          onSuccess: note_0 => {
            if (note_0 && onNoteCreate) {
              onNoteCreate(note_0);
            }
            captureFlagged();
            setIsOpen(false);
          }
        });
      }
      setIsSubmitting(false);
    };
    $[33] = createQuickNoteMutation;
    $[34] = currentUser;
    $[35] = deferPersistence;
    $[36] = event?.id;
    $[37] = existingNote;
    $[38] = onNoteCreate;
    $[39] = onNoteUpdate;
    $[40] = patient;
    $[41] = setIsOpen;
    $[42] = showAlertToggle;
    $[43] = updateQuickNoteMutation;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const handleSave = t20;
  const t21 = !nonModal || deferPersistence;
  let t22;
  if ($[45] !== children || $[46] !== isOpen) {
    t22 = children && <DialogPrimitives.Trigger asChild={true} onClick={e => {
      if (isOpen) {
        e.preventDefault();
      }
    }}>{children}</DialogPrimitives.Trigger>;
    $[45] = children;
    $[46] = isOpen;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <DialogPrimitives.Overlay className="fixed inset-0 z-50 bg-background/15 backdrop-blur-[1px]" />;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_1 => {
      if (!windowRef.current?.contains(document.activeElement)) {
        e_1.preventDefault();
      }
    };
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== position) {
    t25 = position ? {
      left: position.x,
      top: position.y
    } : undefined;
    $[50] = position;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const t26 = !position && "bottom-2 right-2 sm:bottom-6 sm:right-6";
  let t27;
  if ($[52] !== t26) {
    t27 = cx("fixed z-50 flex max-h-[90dvh] w-[calc(100vw-16px)] max-w-lg flex-col rounded-xl border border-border p-1 shadow-2xl", t26, "bg-background/95 backdrop-blur-3xl", "motion-preset-fade-md", focusRing);
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  const t28 = existingNote ? "Edit" : "Add";
  let t29;
  if ($[54] !== t28) {
    t29 = <DialogTitle className="text-h2"><span>{t28}</span> Memo</DialogTitle>;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <MdDragIndicator className="mt-1 h-5 w-5 shrink-0 text-foreground-muted" aria-hidden="true" />;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== t29) {
    t31 = <div className="flex items-start justify-between gap-2">{t29}{t30}</div>;
    $[57] = t29;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== appointmentTitle || $[60] !== event || $[61] !== existingNote || $[62] !== patient) {
    t32 = patient && <DialogDescription>{existingNote ? `Edit your note for ${formatName(patient.firstName, patient.lastName)}${event ? ` for their appointment "${appointmentTitle}"` : ""} created on ${formatDate(existingNote.createdDate)}.` : `Create a note for ${formatName(patient.firstName, patient.lastName)}${event ? ` for their appointment "${appointmentTitle}" on ${formatDateTime(event.start)}` : ""}.`}</DialogDescription>;
    $[59] = appointmentTitle;
    $[60] = event;
    $[61] = existingNote;
    $[62] = patient;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] !== onPointerDown || $[65] !== t31 || $[66] !== t32) {
    t33 = <DialogHeader className="cursor-grab touch-none" onPointerDown={onPointerDown}>{t31}{t32}</DialogHeader>;
    $[64] = onPointerDown;
    $[65] = t31;
    $[66] = t32;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== selectedPatient || $[69] !== showPatientSearch) {
    t34 = showPatientSearch && <div><Label className="text-body font-medium">Select Patient</Label><div className="mt-2"><PatientSearch patient={selectedPatient} onPatientSelect={setSelectedPatient} placeholder="Search for a patient..." /></div></div>;
    $[68] = selectedPatient;
    $[69] = showPatientSearch;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  let t35;
  if ($[71] !== noteFieldId) {
    t35 = <Label htmlFor={noteFieldId}>Note Content</Label>;
    $[71] = noteFieldId;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      required: "Note is required"
    };
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== existingNote || $[75] !== isSubmitting || $[76] !== noteFieldId) {
    t37 = t38 => {
      const {
        field
      } = t38;
      return <Textarea {...field} id={noteFieldId} placeholder={existingNote ? "Edit your note here..." : "Write your note here..."} className="min-h-[120px] resize-none" disabled={isSubmitting} />;
    };
    $[74] = existingNote;
    $[75] = isSubmitting;
    $[76] = noteFieldId;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== control || $[79] !== t37) {
    t38 = <Controller name="note" control={control} rules={t36} render={t37} />;
    $[78] = control;
    $[79] = t37;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  let t39;
  if ($[81] !== errors.note) {
    t39 = errors.note && <p className="text-body text-status-error-text">{errors.note.message}</p>;
    $[81] = errors.note;
    $[82] = t39;
  } else {
    t39 = $[82];
  }
  let t40;
  if ($[83] !== t35 || $[84] !== t38 || $[85] !== t39) {
    t40 = <div className="space-y-2">{t35}{t38}{t39}</div>;
    $[83] = t35;
    $[84] = t38;
    $[85] = t39;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  let t41;
  if ($[87] !== alertFieldId || $[88] !== control || $[89] !== isSubmitting || $[90] !== showAlertToggle) {
    t41 = showAlertToggle && <div className="flex items-start justify-between gap-4"><div><Label htmlFor={alertFieldId}>Show as an alert</Label><p className="mt-1 text-caption text-muted-foreground">Pops this memo up whenever someone opens this patient's chart, until they close it.</p></div><Controller name="isAlert" control={control} render={t42 => {
        const {
          field: field_0
        } = t42;
        return <Switch id={alertFieldId} checked={field_0.value} onCheckedChange={field_0.onChange} disabled={isSubmitting} />;
      }} /></div>;
    $[87] = alertFieldId;
    $[88] = control;
    $[89] = isSubmitting;
    $[90] = showAlertToggle;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  let t42;
  if ($[92] !== t34 || $[93] !== t40 || $[94] !== t41) {
    t42 = <DialogBody><div className="space-y-4">{t34}{t40}{t41}</div></DialogBody>;
    $[92] = t34;
    $[93] = t40;
    $[94] = t41;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== setIsOpen) {
    t43 = <Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button>;
    $[96] = setIsOpen;
    $[97] = t43;
  } else {
    t43 = $[97];
  }
  let t44;
  if ($[98] !== handleSave || $[99] !== handleSubmit) {
    t44 = handleSubmit(handleSave);
    $[98] = handleSave;
    $[99] = handleSubmit;
    $[100] = t44;
  } else {
    t44 = $[100];
  }
  const t45 = !patient || isSubmitting;
  const t46 = isSubmitting ? existingNote ? "Updating..." : "Saving..." : existingNote ? "Update Memo" : "Save Memo";
  let t47;
  if ($[101] !== t44 || $[102] !== t45 || $[103] !== t46) {
    t47 = <Button onClick={t44} disabled={t45}>{t46}</Button>;
    $[101] = t44;
    $[102] = t45;
    $[103] = t46;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  if ($[105] !== t43 || $[106] !== t47) {
    t48 = <DialogFooter>{t43}{t47}</DialogFooter>;
    $[105] = t43;
    $[106] = t47;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  let t49;
  if ($[108] !== onPointerCancel || $[109] !== onPointerMove || $[110] !== onPointerUp || $[111] !== setWindowRef || $[112] !== t25 || $[113] !== t27 || $[114] !== t33 || $[115] !== t42 || $[116] !== t48) {
    t49 = <DialogPrimitives.Portal>{t23}<DialogPrimitives.Content ref={setWindowRef} onInteractOutside={_temp2} onEscapeKeyDown={t24} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} style={t25} className={t27}>{t33}{t42}{t48}</DialogPrimitives.Content></DialogPrimitives.Portal>;
    $[108] = onPointerCancel;
    $[109] = onPointerMove;
    $[110] = onPointerUp;
    $[111] = setWindowRef;
    $[112] = t25;
    $[113] = t27;
    $[114] = t33;
    $[115] = t42;
    $[116] = t48;
    $[117] = t49;
  } else {
    t49 = $[117];
  }
  let t50;
  if ($[118] !== isOpen || $[119] !== setIsOpen || $[120] !== t21 || $[121] !== t22 || $[122] !== t49) {
    t50 = <DialogPrimitives.Root open={isOpen} onOpenChange={setIsOpen} modal={t21}>{t22}{t49}</DialogPrimitives.Root>;
    $[118] = isOpen;
    $[119] = setIsOpen;
    $[120] = t21;
    $[121] = t22;
    $[122] = t49;
    $[123] = t50;
  } else {
    t50 = $[123];
  }
  return t50;
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp(attendee) {
  return attendee.user.type === UserType.PATIENT;
}